import axios from 'axios';
import { checkForErrors } from './lib/error';
/**
 * Barebones client for interacting with the search service.
 *
 * Requires passing in the various query functions. If you'd
 * rather use the fully-configured client, use {@link Search}
 * instead.
 *
 * @category Clients
 *
 * @example
 * ```typescript
 * import { SearchClient, getSlots } from '@kyruus/search-sdk';
 *
 * // Instantiate the search client
 * const searchClient = new SearchClient({
 *   version: 'v9',
 *   authToken
 * });
 *
 * // Execute a query using a provided query function
 * const results = await searchClient.query(getSlots, { perPage: 25 });
 * ```
 */
export class SearchClient {
    /**
     * @param {ClientConfig} config the search client configuration object
     */
    constructor({ baseURL = '/api', headers = {}, version = 'v9', customerCode, productName, authToken }) {
        let _headers = headers;
        if (customerCode) {
            _headers = Object.assign(Object.assign({}, _headers), { 'X-Consumer-Groups': customerCode });
        }
        if (authToken) {
            _headers = Object.assign(Object.assign({}, _headers), { Authorization: authToken });
        }
        if (productName) {
            _headers = Object.assign(Object.assign({}, _headers), { 'X-Consumer-Username': productName });
        }
        this._config = {
            baseURL,
            headers: _headers,
            version,
            customerCode,
            authToken
        };
        this._client = axios.create(this._config);
        // Set up error handling
        // response.use takes success callback as first param, error callback as second
        // therefore, need to check for errors in success case (warnings with 200 status)
        // and also catch when an error status comes back
        this._client.interceptors.response.use(checkForErrors, (error) => error.response && checkForErrors(error.response));
        // merge interceptors from global axios client with internal axios client
        axios.interceptors.request.handlers.forEach((handler) => this._client.interceptors.request.use(handler.fulfilled, handler.rejected));
        axios.interceptors.response.handlers.forEach((handler) => this._client.interceptors.response.use(handler.fulfilled, handler.rejected));
    }
    /** Function for executing the given query function.
     *
     * @param queryFn the query function to execute
     * @param input input parameters for paring down the search results
     */
    query(queryFn, input) {
        return queryFn(this._client, this._config, input);
    }
}
//# sourceMappingURL=client.js.map